/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ScanPartContext } from '@kaiizen/kai-scan-template-system';
import { useEffect, useRef, useState } from 'react';

import { KAI_SHIKI_TYPOGRAPHY, styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { LAYOUT2 } from '@/Client/Styles/LAYOUT2';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { CommonActionButton, CommonSegmentedToggle } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import type { GalleryPhotoTarget } from './galleryPhotoOperations';

type GalleryMoveCopyType = 'move' | 'copy';

const OPERATION_OPTIONS: readonly {
  value: GalleryMoveCopyType;
  label: string;
}[] = [
  { value: 'move', label: 'Move' },
  { value: 'copy', label: 'Copy' },
];
const DISABLED_OPERATION_OPTIONS = OPERATION_OPTIONS.map(option => ({
  ...option,
  disabled: true,
}));

const PICKER_STYLE = css`
  label: PICKER_STYLE;
  position: absolute;
  right: 20px;
  bottom: 60px;
  z-index: 41;
  width: 300px;
  max-width: calc(100% - 40px);
  max-height: calc(100% - 130px);
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid ${styleguideColors.ink};
  background: ${styleguideColors.white};
  color: ${styleguideColors.ink};
  box-shadow: 0 8px 28px rgba(26, 26, 26, 0.22);
`;

const HEADER_STYLE = css`
  label: HEADER_STYLE;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
`;

const HEADING_STYLE = css`
  label: HEADING_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.mediumBold}
  margin: 0;
`;

const COUNT_STYLE = css`
  label: COUNT_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.xSmall}
  color: var(--text2);
  white-space: nowrap;
`;

const OPERATION_TOGGLE_STYLE = css`
  label: OPERATION_TOGGLE_STYLE;
  padding: 0 12px 12px;

  [role='tablist'] {
    display: flex !important;
    width: 100%;
    border-radius: 0 !important;
  }

  [role='tab'] {
    flex: 1;
    border-radius: 0 !important;
  }
`;

const DESTINATION_SECTION_STYLE = css`
  label: DESTINATION_SECTION_STYLE;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 5px;
  padding: 0 12px 12px;
`;

const DESTINATION_LABEL_STYLE = css`
  label: DESTINATION_LABEL_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.xSmallBold}
  color: var(--text2);
`;

const TARGET_HIERARCHY_STYLE = css`
  label: TARGET_HIERARCHY_STYLE;
  max-height: 260px;
  display: grid;
  align-content: start;
  overflow-y: auto;
  border-top: 1px solid ${styleguideColors.stroke};
  border-bottom: 1px solid ${styleguideColors.stroke};

  [data-gallery-target-depth='1'] {
    width: calc(100% - 14px);
    margin-left: 14px;
  }

  [data-gallery-target-depth='1'] > span:first-of-type {
    font-size: ${pxToRem(12.5)};
    font-weight: 400;
  }
`;

const TARGET_ROW_STYLE = css`
  label: TARGET_ROW_STYLE;
  width: 100%;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  justify-content: stretch !important;
  gap: 10px;
  min-height: 0 !important;
  padding: 8px 10px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  text-align: left !important;
  text-transform: none !important;
  white-space: nowrap !important;
`;

const TARGET_TITLE_STYLE = css`
  label: TARGET_TITLE_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TARGET_SUBTITLE_STYLE = css`
  label: TARGET_SUBTITLE_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.xSmall}
  max-width: ${LAYOUT2.spacing14}px;
  overflow: hidden;
  color: var(--text2);
  text-align: right;
  text-overflow: ellipsis;
`;

const ACTIONS_STYLE = css`
  label: ACTIONS_STYLE;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${styleguideColors.stroke};
`;

const SELECTED_TARGET_ROW_STYLE = css`
  label: SELECTED_TARGET_ROW_STYLE;
  background: ${styleguideColors.sunken} !important;
`;

const FOOTER_ACTION_STYLE = {
  minHeight: 32,
  border: `1px solid ${styleguideColors.stroke}`,
  borderRadius: 0,
  background: styleguideColors.white,
  color: styleguideColors.ink,
  fontFamily: styleguideFontFamily.mono,
  fontSize: pxToRem(10),
} as const;

const APPLY_ACTION_STYLE = {
  ...FOOTER_ACTION_STYLE,
  borderColor: styleguideColors.accent,
  background: styleguideColors.accent,
} as const;

const getApplyLabel = (
  type: GalleryMoveCopyType,
  selectedCount: number,
): string => `${type === 'move' ? 'Move' : 'Copy'} ${selectedCount} ${selectedCount === 1 ? 'photo' : 'photos'}`;

/** Lets Gallery users choose a Move or Copy destination before explicitly applying it. */
export const GalleryMoveCopyPicker = ({
  selectedCount,
  targets,
  canApply,
  onApply,
  onClose,
}: {
  selectedCount: number;
  targets: readonly GalleryPhotoTarget[];
  canApply: (type: GalleryMoveCopyType, target: ScanPartContext) => boolean;
  onApply: (
    type: GalleryMoveCopyType,
    target: ScanPartContext,
  ) => void | Promise<void>;
  onClose: () => void;
}) => {
  const [type, setType] = useState<GalleryMoveCopyType>('move');
  const [selectedTarget, setSelectedTarget] = useState<GalleryPhotoTarget>();
  const [applyPending, setApplyPending] = useState(false);
  const applyPendingRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstTargetRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const returnFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : undefined;
    (firstTargetRef.current ?? headingRef.current)?.focus();
    return () => {
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, []);
  useEffect(() =>
    setSelectedTarget(selected => (
      targets.find(target => target.key === selected?.key)
    )), [targets]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      if (!applyPendingRef.current) onClose();
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [onClose]);

  const apply = async () => {
    if (!selectedTarget || applyPendingRef.current) return;
    applyPendingRef.current = true;
    setApplyPending(true);
    try {
      await onApply(type, selectedTarget.context);
    } finally {
      applyPendingRef.current = false;
      setApplyPending(false);
    }
  };

  return (
    <section
      css={PICKER_STYLE}
      role='dialog'
      aria-labelledby='gallery-move-copy-title'
    >
      <header css={HEADER_STYLE}>
        <h2
          id='gallery-move-copy-title'
          ref={headingRef}
          tabIndex={-1}
          css={HEADING_STYLE}
        >
          Move or copy photos
        </h2>
        <span css={COUNT_STYLE}>
          {selectedCount} {selectedCount === 1 ? 'photo' : 'photos'} selected
        </span>
      </header>
      <div css={OPERATION_TOGGLE_STYLE}>
        <CommonSegmentedToggle
          value={type}
          options={applyPending ? DISABLED_OPERATION_OPTIONS : OPERATION_OPTIONS}
          onChange={setType}
        />
      </div>
      <div css={DESTINATION_SECTION_STYLE}>
        <span css={DESTINATION_LABEL_STYLE}>Destination</span>
        <div css={TARGET_HIERARCHY_STYLE} aria-label='Photo destinations'>
          {targets.map((target, index) => (
            <CommonActionButton
              key={target.key}
              ref={index === 0 ? firstTargetRef : undefined}
              css={[
                TARGET_ROW_STYLE,
                selectedTarget?.key === target.key && SELECTED_TARGET_ROW_STYLE,
              ]}
              data-gallery-target-depth={target.depth}
              aria-label={[target.title, target.subtitle]
                .filter(Boolean)
                .join(', ')}
              aria-pressed={selectedTarget?.key === target.key}
              disabled={applyPending}
              onClick={() => setSelectedTarget(target)}
            >
              <span css={TARGET_TITLE_STYLE}>{target.title}</span>
              {target.subtitle ? <span css={TARGET_SUBTITLE_STYLE}>{target.subtitle}</span> : null}
            </CommonActionButton>
          ))}
        </div>
      </div>
      <div
        css={ACTIONS_STYLE}
        role='group'
        aria-label='Move or copy actions'
      >
        <CommonActionButton style={FOOTER_ACTION_STYLE} disabled={applyPending} onClick={onClose}>
          Cancel
        </CommonActionButton>
        <CommonActionButton
          style={APPLY_ACTION_STYLE}
          disabled={applyPending
            || !selectedTarget
            || !canApply(type, selectedTarget.context)}
          onClick={apply}
        >
          {getApplyLabel(type, selectedCount)}
        </CommonActionButton>
      </div>
    </section>
  );
};
